/**
 * ПОЛНЫЙ ПУТЬ: D:\QR\QR-WEB\dashboard\src\components\Admin\AdminBusinesses\AdminApprove.js
 * ВЕРСИЯ: 1.0.0
 * ВРЕМЯ СОЗДАНИЯ: 15.11.2025 17:30
 * 
 * ОПИСАНИЕ: Компонент для одобрения/отклонения заявок бизнеса
 * - Показывает список бизнесов на проверку
 * - Детальная информация о каждой заявке
 * - Кнопки одобрения и отклонения
 * - Комментарии администратора
 */

import React, { useState, useEffect } from 'react';
import './AdminApprove.css';

const AdminApprove = () => {
  const [businesses, setBusinesses] = useState([]);
  const [filteredBusinesses, setFilteredBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [adminComment, setAdminComment] = useState('');
  const [processLoading, setProcessLoading] = useState(false);

  useEffect(() => {
    fetchPendingBusinesses();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [businesses, searchQuery]);

  const fetchPendingBusinesses = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('authToken');
      const response = await fetch('https://qr-money.onrender.com/api/admin/businesses?status=pending', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      const businessList = data.data || data.businesses || data || [];
      const pending = Array.isArray(businessList) 
        ? businessList.filter(b => b.status === 'pending')
        : [];
      
      setBusinesses(pending);
      console.log('✅ Заявки загружены:', pending);
    } catch (err) {
      console.error('❌ Ошибка при загрузке заявок:', err);
      setError(err.message);
      setBusinesses(getMockPendingBusinesses());
    } finally {
      setLoading(false);
    }
  };

  const getMockPendingBusinesses = () => [
    {
      id: 1,
      user_id: 2,
      email: 'newbusiness@test.com',
      company_name: 'New Business LLC',
      status: 'pending',
      tier: 1,
      commission: 3.0,
      created_at: '2025-11-14T10:00:00Z',
      description: 'Мы занимаемся розничной торговлей электроникой',
      website: 'https://example.com',
      tax_id: '1234567890',
      phone: '+1234567890'
    },
    {
      id: 2,
      user_id: 3,
      email: 'startup@test.com',
      company_name: 'Tech Startup Inc',
      status: 'pending',
      tier: 1,
      commission: 2.5,
      created_at: '2025-11-13T15:30:00Z',
      description: 'Разработка мобильных приложений',
      website: 'https://startup.example.com',
      tax_id: '0987654321',
      phone: '+0987654321'
    }
  ];

  const applyFilters = () => {
    let filtered = businesses;

    if (searchQuery.trim()) {
      filtered = filtered.filter(b =>
        b.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
        b.company_name.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    setFilteredBusinesses(filtered);
  };

  const handleApproveBusiness = async () => {
    if (!selectedBusiness) return;

    if (!window.confirm(`Вы уверены что хотите одобрить "${selectedBusiness.company_name}"?`)) {
      return;
    }

    try {
      setProcessLoading(true);
      const token = localStorage.getItem('authToken');
      
      const response = await fetch(`https://qr-money.onrender.com/api/admin/businesses/${selectedBusiness.id}/approve`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          comment: adminComment,
          tier: selectedBusiness.tier,
          commission: selectedBusiness.commission
        })
      });

      if (response.ok) {
        setBusinesses(businesses.filter(b => b.id !== selectedBusiness.id));
        setSelectedBusiness(null);
        setAdminComment('');
        alert('✅ Бизнес одобрен');
      } else {
        const errData = await response.json();
        alert(`❌ Ошибка: ${errData.message || 'при одобрении'}`);
      }
    } catch (err) {
      console.error('❌ Ошибка:', err);
      alert('❌ Ошибка при одобрении бизнеса');
    } finally {
      setProcessLoading(false);
    }
  };

  const handleRejectBusiness = async () => {
    if (!selectedBusiness) return;

    if (!adminComment.trim()) {
      alert('⚠️ Пожалуйста укажите причину отклонения');
      return;
    }

    if (!window.confirm(`Вы уверены что хотите отклонить "${selectedBusiness.company_name}"?`)) {
      return;
    }

    try {
      setProcessLoading(true);
      const token = localStorage.getItem('authToken');

      const response = await fetch(`https://qr-money.onrender.com/api/admin/businesses/${selectedBusiness.id}/reject`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          reason: adminComment
        })
      });

      if (response.ok) {
        setBusinesses(businesses.filter(b => b.id !== selectedBusiness.id));
        setSelectedBusiness(null);
        setAdminComment('');
        alert('✅ Бизнес отклонен');
      } else {
        const errData = await response.json();
        alert(`❌ Ошибка: ${errData.message || 'при отклонении'}`);
      }
    } catch (err) {
      console.error('❌ Ошибка:', err);
      alert('❌ Ошибка при отклонении бизнеса');
    } finally {
      setProcessLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-approve-page">
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <p>⏳ Загрузка заявок...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-approve-page">
      {/* ЗАГОЛОВОК */}
      <div className="approve-header">
        <h1>📝 Одобрение заявок бизнеса</h1>
        <p>В очереди: {businesses.length} | Показано: {filteredBusinesses.length}</p>
      </div>

      {/* ОШИБКА */}
      {error && (
        <div className="error-message">
          ⚠️ {error} (используются тестовые данные)
        </div>
      )}

      <div className="approve-container">
        {/* СПИСОК ЗАЯВОК */}
        <div className="approve-list">
          <div className="list-header">
            <input
              type="text"
              className="search-input"
              placeholder="🔍 Поиск по email или названию..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <button
              className="refresh-btn"
              onClick={fetchPendingBusinesses}
              title="Обновить список"
            >
              🔄
            </button>
          </div>

          <div className="businesses-list">
            {filteredBusinesses.length > 0 ? (
              filteredBusinesses.map((business) => (
                <div
                  key={business.id}
                  className={`business-item ${selectedBusiness?.id === business.id ? 'active' : ''}`}
                  onClick={() => {
                    setSelectedBusiness(business);
                    setAdminComment('');
                  }}
                >
                  <div className="business-item-header">
                    <div className="business-item-title">
                      <h3>{business.company_name}</h3>
                      <span className="business-id">ID: {business.id}</span>
                    </div>
                    <div className="business-item-status">
                      ⏳ На проверку
                    </div>
                  </div>
                  <div className="business-item-info">
                    <p><strong>Email:</strong> {business.email}</p>
                    <p><strong>Дата:</strong> {new Date(business.created_at).toLocaleDateString('ru-RU')}</p>
                  </div>
                </div>
              ))
            ) : (
              <div className="no-items">
                <p>✅ Все заявки обработаны!</p>
              </div>
            )}
          </div>
        </div>

        {/* ДЕТАЛИ И ДЕЙСТВИЯ */}
        <div className="approve-details">
          {selectedBusiness ? (
            <>
              <div className="details-header">
                <h2>Детали заявки</h2>
              </div>

              <div className="details-content">
                {/* ОСНОВНАЯ ИНФОРМАЦИЯ */}
                <div className="details-section">
                  <h3>📋 Основная информация</h3>
                  <div className="info-grid">
                    <div className="info-item">
                      <label>Компания</label>
                      <p>{selectedBusiness.company_name}</p>
                    </div>
                    <div className="info-item">
                      <label>Email</label>
                      <p>{selectedBusiness.email}</p>
                    </div>
                    <div className="info-item">
                      <label>Телефон</label>
                      <p>{selectedBusiness.phone || 'N/A'}</p>
                    </div>
                    <div className="info-item">
                      <label>Tax ID</label>
                      <p>{selectedBusiness.tax_id || 'N/A'}</p>
                    </div>
                    <div className="info-item">
                      <label>Веб-сайт</label>
                      <p>
                        {selectedBusiness.website ? (
                          <a href={selectedBusiness.website} target="_blank" rel="noopener noreferrer">
                            {selectedBusiness.website}
                          </a>
                        ) : (
                          'N/A'
                        )}
                      </p>
                    </div>
                    <div className="info-item">
                      <label>Дата создания</label>
                      <p>{new Date(selectedBusiness.created_at).toLocaleDateString('ru-RU')}</p>
                    </div>
                  </div>
                </div>

                {/* ОПИСАНИЕ */}
                {selectedBusiness.description && (
                  <div className="details-section">
                    <h3>📝 Описание</h3>
                    <p className="description-text">{selectedBusiness.description}</p>
                  </div>
                )}

                {/* ПАРАМЕТРЫ */}
                <div className="details-section">
                  <h3>⚙️ Параметры</h3>
                  <div className="params-grid">
                    <div className="param-item">
                      <label>Уровень (Tier)</label>
                      <input
                        type="number"
                        value={selectedBusiness.tier}
                        readOnly
                        className="param-input"
                      />
                    </div>
                    <div className="param-item">
                      <label>Комиссия (%)</label>
                      <input
                        type="number"
                        step="0.1"
                        value={selectedBusiness.commission}
                        readOnly
                        className="param-input"
                      />
                    </div>
                  </div>
                </div>

                {/* КОММЕНТАРИЙ АДМИНА */}
                <div className="details-section">
                  <h3>💬 Комментарий администратора</h3>
                  <textarea
                    className="admin-comment"
                    placeholder="Введите комментарий или причину отклонения..."
                    value={adminComment}
                    onChange={(e) => setAdminComment(e.target.value)}
                    rows="4"
                  />
                </div>

                {/* КНОПКИ ДЕЙСТВИЙ */}
                <div className="action-buttons">
                  <button
                    className="btn btn-approve"
                    onClick={handleApproveBusiness}
                    disabled={processLoading}
                  >
                    ✅ {processLoading ? 'Обработка...' : 'Одобрить'}
                  </button>
                  <button
                    className="btn btn-reject"
                    onClick={handleRejectBusiness}
                    disabled={processLoading}
                  >
                    ❌ {processLoading ? 'Обработка...' : 'Отклонить'}
                  </button>
                </div>
              </div>
            </>
          ) : (
            <div className="no-selection">
              <p>👈 Выберите заявку для просмотра деталей</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminApprove;
